import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { Mail, Instagram, Facebook, Linkedin, MapPin, Send, MessageCircle } from 'lucide-react';

export const ContactSection: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-vilarias-dark overflow-hidden">
      <div className="absolute inset-0 bg-hero-glow opacity-30" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-vilarias-accent/5 rounded-full blur-[150px]" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.contact.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto" />
        </motion.div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="space-y-6">
              <a
                href={`https://wa.me/${t.contact.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card rounded-xl p-5 flex items-center gap-4 hover:border-vilarias-accent/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 group-hover:scale-110 transition-transform">
                  <MessageCircle size={22} />
                </div>
                <div>
                  <p className="text-xs text-vilarias-text-secondary uppercase tracking-wider">WhatsApp</p>
                  <p className="text-white">{t.contact.whatsapp}</p>
                </div>
              </a>

              <a
                href={`mailto:${t.contact.email}`}
                className="glass-card rounded-xl p-5 flex items-center gap-4 hover:border-vilarias-accent/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-vilarias-accent/20 flex items-center justify-center text-vilarias-accent group-hover:scale-110 transition-transform">
                  <Mail size={22} />
                </div>
                <div>
                  <p className="text-xs text-vilarias-text-secondary uppercase tracking-wider">Email</p>
                  <p className="text-white">{t.contact.email}</p>
                </div>
              </a>

              <div className="glass-card rounded-xl p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-vilarias-glow/20 flex items-center justify-center text-vilarias-glow">
                  <MapPin size={22} />
                </div>
                <div>
                  <p className="text-xs text-vilarias-text-secondary uppercase tracking-wider">Location</p>
                  <p className="text-white">Tenerife, Canary Islands</p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                {[
                  { icon: <Instagram size={20} />, label: 'Instagram', href: '#' },
                  { icon: <Facebook size={20} />, label: 'Facebook', href: '#' },
                  { icon: <Linkedin size={20} />, label: 'LinkedIn', href: '#' },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="w-12 h-12 rounded-full glass-card flex items-center justify-center text-vilarias-text-secondary hover:text-white hover:border-vilarias-accent/50 transition-all"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-vilarias-accent/20 flex items-center justify-center mx-auto mb-4">
                    <Send size={28} className="text-vilarias-accent" />
                  </div>
                  <p className="text-white text-lg mb-2">Message Sent!</p>
                  <p className="text-vilarias-text-secondary text-sm">We will get back to you shortly.</p>
                </div>
              ) : (
                <>
                  <div className="mb-5">
                    <label className="text-xs text-vilarias-text-secondary uppercase tracking-wider mb-2 block">
                      {t.contact.form.name}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-vilarias-black border border-vilarias-border rounded-lg px-4 py-3 text-white focus:border-vilarias-accent focus:outline-none transition-colors"
                    />
                  </div>
                  <div className="mb-5">
                    <label className="text-xs text-vilarias-text-secondary uppercase tracking-wider mb-2 block">
                      {t.contact.form.email}
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-vilarias-black border border-vilarias-border rounded-lg px-4 py-3 text-white focus:border-vilarias-accent focus:outline-none transition-colors"
                    />
                  </div>
                  <div className="mb-6">
                    <label className="text-xs text-vilarias-text-secondary uppercase tracking-wider mb-2 block">
                      {t.contact.form.message}
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-vilarias-black border border-vilarias-border rounded-lg px-4 py-3 text-white focus:border-vilarias-accent focus:outline-none transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-vilarias-accent text-white rounded-lg btn-glow text-sm tracking-wide uppercase font-medium flex items-center justify-center gap-2"
                  >
                    <Send size={16} />
                    {t.contact.form.submit}
                  </button>
                </>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
